// Small, self-contained helpers for the "get the native app" prompt shown on the website.
// Both apps are live: Android on Google Play, iOS on the App Store. A single smart landing at
// /get routes each visitor to the right place (and shows a QR on desktop); the banner and the
// You-menu entry send phone users straight to their store and desktop users to /get.

export const PLAY_URL = "https://play.google.com/store/apps/details?id=com.insnip.app";
export const APP_STORE_URL = "https://apps.apple.com/app/id6792640319";
export const GET_PATH = "/get";

export type WebPlatform = "android" | "ios" | "desktop";

/** The best "get the app" destination for a visitor: their own store on a phone, else the smart
 *  /get landing (QR on desktop). Keeps every download CTA one tap from the right place. */
export function storeHref(platform: WebPlatform = webPlatform()): string {
  if (platform === "android") return PLAY_URL;
  if (platform === "ios") return APP_STORE_URL;
  return GET_PATH;
}

/** Coarse platform from the User-Agent, for the WEB app only (native app is detected separately). */
/**
 * A PHONE browser, specifically: the surface where the reader is replaced by the app wall.
 * Phones only, by user agent, because the wall's promise ("built for your pocket") is only
 * true there:
 *  - the native app itself must never see it (isNativeApp guard; the app IS this codebase),
 *  - tablets and iPads keep the full web reader (the desktop layout genuinely works there,
 *    and iPadOS reports a Mac UA anyway, so a viewport-width test would misfire on every
 *    resized desktop window),
 *  - desktop keeps the reader untouched.
 */
export function isPhoneBrowser(uaIn?: string): boolean {
  const ua = uaIn ?? (typeof navigator === "undefined" ? "" : navigator.userAgent || "");
  if (!ua) return false;
  if (/iphone|ipod/i.test(ua)) return true;
  // Android phones carry "Mobile" in the UA; Android tablets do not.
  return /android/i.test(ua) && /mobile/i.test(ua);
}

export function webPlatform(): WebPlatform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  // iPadOS 13+ reports as Mac; treat touch-Macs as iOS too.
  if (/iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document)) return "ios";
  return "desktop";
}

const DISMISS_KEY = "insnip.appPromoDismissed";

export function appPromoDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissAppPromo(): void {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* storage unavailable, banner simply reappears next session */
  }
}

/**
 * Crawlers and preview bots. There is no server-side twin of this rule: the SEO surfaces
 * (/c/, /play, /brief, the homepage) are Pages Functions that never mount the SPA, so the
 * wall decision only exists in the browser, and this is its one copy. The decision table
 * lives in appPromo.wall.test.ts; extend the corpus there first.
 *
 * Failing OPEN is the rule. A bot that slips through sees the reader, which costs nothing.
 * A bot wrongly walled could cost indexing or break a shared link's preview render.
 */
const CRAWLER_RE =
  /bot|crawl|spider|slurp|bingpreview|googlebot|google-inspectiontool|google-read-aloud|adsbot|mediapartners|applebot|duckduckgo|yandex|baidu|sogou|facebookexternalhit|facebookcatalog|twitterbot|linkedinbot|whatsapp|telegram|discord|slack|embedly|quora|pinterest|redditbot|skypeuripreview|vkshare|w3c_validator|lighthouse|chrome-lighthouse|pagespeed|gtmetrix|headlesschrome|preview|scraper|fetcher|monitor|uptime|pingdom|curl|wget|python-requests|axios|node-fetch|okhttp|java\/|go-http-client/i;

export function isCrawlerBrowser(ua?: string): boolean {
  const s = ua ?? (typeof navigator === "undefined" ? "" : navigator.userAgent || "");
  return CRAWLER_RE.test(s);
}

/** The escape hatch, spelled the same as the server's: ?desktop=1 always wins. */
export function wallBypassed(search?: string): boolean {
  const s = search ?? (typeof location === "undefined" ? "" : location.search || "");
  return s.indexOf("desktop=1") !== -1;
}

/**
 * Should this visitor get the app wall instead of the reader?
 *
 * Every clause is a way of NOT showing it, because every false positive is a reader who came
 * for a story and got a sales pitch:
 *  - the native app is this same bundle, and walling it would wall the product itself,
 *  - an installed PWA is someone who already took the install, so asking again is an insult,
 *  - crawlers and unfurlers keep the reader (see isCrawlerBrowser),
 *  - ?desktop=1 lets a real phone inspect the reader without editing code,
 *  - tablets and desktop are untouched (isPhoneBrowser is phones only).
 */
export function shouldShowAppWall(opts: {
  nativeApp: boolean;
  standalone: boolean;
  ua?: string;
  search?: string;
}): boolean {
  if (opts.nativeApp || opts.standalone) return false;
  if (wallBypassed(opts.search)) return false;
  if (isCrawlerBrowser(opts.ua)) return false;
  if (!WALL_ENABLED) return false;
  return isPhoneBrowser(opts.ua);
}

/**
 * THE WALL IS OFF (2026-08-25, before the public launch).
 *
 * Measured on production the same day: a Pixel-class phone opening insnip.com/read got
 * "The reader lives in the app" and not one story. Every link shared to Reddit, to a group
 * chat or anywhere else is opened mostly on phones, so the wall was standing between the
 * launch and its own audience, one tap after they arrived. The reader now serves phones,
 * and the app is asked for by the install banner and the /get page instead, after someone
 * has read something worth installing for.
 *
 * The wall itself is kept whole (component, tests, the decision table above) so turning it
 * back on is this one constant, not an archaeology exercise. See also functions/read.js,
 * which documents the server side of the same decision.
 */
const WALL_ENABLED = false;
